import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Typography,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Grid,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton,
  FormControlLabel,
  Checkbox,
  Card,
  CardContent,
  Paper,
  useTheme,
  useMediaQuery,
  Stepper,
  Step,
  StepLabel,
  Fade,
  Grow,
  Chip,
  Avatar,
  Divider,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Work as WorkIcon,
  School as SchoolIcon,
  Security as SecurityIcon,
  MonitorHeart as MonitorHeartIcon,
  ArrowForward as ArrowForwardIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Check as CheckIcon,
  Payment,
  Schedule,
  DirectionsRun,
  Accessibility
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { handleApiError } from '../utils/api';
import { motion } from 'framer-motion';
import KvkkApprovalModal from '../components/KvkkApprovalModal';
import FacilityAccessDisplay from '../components/FacilityAccessDisplay';

const API_URL = process.env.REACT_APP_API_URL || '/api';

// Left side welcome panel - moved outside to prevent re-creation
const WelcomePanel = React.memo(() => {
  const theme = useTheme();
  const { t } = useTranslation();
  
  return (
    <Box
      sx={{
        background: `linear-gradient(135deg, ${theme.palette.primary[900]} 0%, ${theme.palette.primary[800]} 35%, ${theme.palette.primary[700]} 100%)`,
        color: 'white',
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        position: 'relative',
        overflow: 'hidden'
      }}
    >
      {/* Background decoration */}
      <Fade in timeout={1000}>
        <Box
          sx={{
            position: 'absolute',
            width: '300px',
            height: '300px',
            borderRadius: '50%',
            background: theme.palette.accent.mint,
            filter: 'blur(80px)',
            opacity: 0.2,
            top: '-50px',
            right: '-50px',
            zIndex: 1
          }}
        />
      </Fade>

      <Fade in timeout={1500}>
        <Box
          sx={{
            position: 'absolute',
            width: '250px',
            height: '250px',
            borderRadius: '50%',
            background: theme.palette.accent.yellow,
            filter: 'blur(70px)',
            opacity: 0.15,
            bottom: '-30px',
            left: '-30px',
            zIndex: 1
          }}
        />
      </Fade>

      <Grow in timeout={1000}>
        <Box sx={{ textAlign: 'center', mb: 4, zIndex: 2 }}>
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            style={{ display: 'flex', justifyContent: 'center', alignItems: 'center'}}
          >
            <img 
              src="/logo512.png" 
              alt="MetuMotion Logo" 
              style={{ 
                width: '180px', 
                height: '180px',
                filter: 'drop-shadow(0px 4px 8px rgba(0, 0, 0, 0.2))'
              }} 
            />
          </motion.div>
          
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.4 }}
          >
            <Typography 
              variant="h2" 
              component="h1" 
              sx={{ 
                fontWeight: 700, 
                mt: 3, 
                mb: 1,
                color: 'white',
                textShadow: '0px 2px 4px rgba(0, 0, 0, 0.2)'
              }}
            >
              MetuMotion
            </Typography>
          </motion.div>
          
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.6 }}
          >
            <Typography 
              variant="h6" 
              sx={{ 
                opacity: 0.9,
                maxWidth: '400px',
                mx: 'auto',
                mt: 2,
                color: 'white'
              }}
            >
              {t('registration:welcomePanel.tagline')}
            </Typography>
          </motion.div>
          
          <Box sx={{ mt: 6, display: 'flex', justifyContent: 'center', gap: 2 }}>
            <motion.div
              initial={{ scale: 0, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.8 }}
            >
              <Box 
                sx={{ 
                  display: 'flex', 
                  flexDirection: 'column', 
                  alignItems: 'center',
                  px: 3
                }}
              >
                <DirectionsRun sx={{ fontSize: 40, color: theme.palette.accent.yellow, mb: 1 }} />
                <Typography variant="body2" sx={{ opacity: 0.9, textAlign: 'center' , color: theme.palette.accent.yellow}}>
                  {t('registration:welcomePanel.features.personalizedPlans')}
                </Typography>
              </Box>
            </motion.div>
            
            <Divider orientation="vertical" flexItem sx={{ bgcolor: 'rgba(255,255,255,0.2)' }} />
            
            <motion.div
              initial={{ scale: 0, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.5, delay: 1 }}
            >
              <Box 
                sx={{ 
                  display: 'flex', 
                  flexDirection: 'column', 
                  alignItems: 'center',
                  px: 3
                }}
              >
                <Accessibility sx={{ fontSize: 40, color: theme.palette.accent.yellow, mb: 1 }} />
                <Typography variant="body2" sx={{ opacity: 0.9, textAlign: 'center' , color: theme.palette.accent.yellow}}>
                  {t('registration:welcomePanel.features.expertTrainers')}
                </Typography>
              </Box>
            </motion.div>
          </Box>

          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.8, delay: 1.2 }}
          >
            <Button
              variant="outlined"
              color="inherit"
              size="large"
              component={RouterLink}
              to="/login"
              sx={{
                mt: 6,
                borderColor: 'rgba(255,255,255,0.5)',
                color: 'white',
                borderRadius: 2,
                px: 4,
                py: 1,
                transition: 'all 0.3s ease',
                '&:hover': {
                  borderColor: '#ffd166',
                  backgroundColor: 'rgba(255, 209, 102, 0.1)',
                  color: '#ffd166',
                  transform: 'translateY(-2px)'
                }
              }}
            >
              {t('registration:welcomePanel.loginLink')}
            </Button>
          </motion.div>
        </Box>
      </Grow>
    </Box>
  );
});

const RegisterPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, isLoading } = useAuth();
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  // Form state
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showKvkkModal, setShowKvkkModal] = useState(false);
  const [pendingRegistrationData, setPendingRegistrationData] = useState(null);
  const [tempSessionToken, setTempSessionToken] = useState(null);

  // Email verification state
  const [verificationToken, setVerificationToken] = useState('');
  const [emailLocked, setEmailLocked] = useState(false);
  
  // Fixed input width
  const inputWidth = isMobile ? "100%" : "300px";
  
  // Registration data
  const [registrationData, setRegistrationData] = useState({
    // Step 1 - Account Details
    username: '',
    email: '',
    password: '',
    confirmPassword: '',
    firstName: '',
    lastName: '',
    userType: '',
    memberType: '',
    tckn: '',
    address: {
      street: '',
      city: '',
      district: '',
      postalCode: ''
    },
    emergencyContact: {
      name: '',
      relationship: '',
      phone: ''
    },
    universityProfile: {
      department: '',
      studentNumber: '',
      employeeId: ''
    },
    healthDisclaimer: {
      accepted: false
    },
    ageConfirmation: {
      accepted: false
    },
    // Step 2 - Plan Selection
    selectedPlan: null,
    // Session management
    sessionToken: '',
    // Registration status
    applicationId: '',
    registrationStatus: ''
  });

  const [availablePlans, setAvailablePlans] = useState([]);
  const [memberTypes, setMemberTypes] = useState([]);
  const [selectedMemberType, setSelectedMemberType] = useState(null);
  const [customFieldsData, setCustomFieldsData] = useState({});

  const steps = [
    { label: t('registration:steps.accountDetails'), icon: PersonIcon },
    { label: t('registration:steps.selectPlan'), icon: Payment },
    { label: t('registration:steps.verification'), icon: Schedule },
    { label: t('registration:steps.complete'), icon: CheckCircleIcon }
  ];

  // Check if email verification is required - defined before usage
  const checkEmailVerificationRequired = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/system-settings/public/email-verification`);
      if (response.data.success) {
        const settings = response.data.data;
        if (settings.emailVerification?.enableEmailVerification &&
            settings.emailVerification?.requireForRegistration) {
          // Redirect to email verification page
          navigate('/email-verification', {
            state: { redirectFrom: 'registration' }
          });
          return;
        }
      }
    } catch (error) {
      console.log('Could not check email verification settings:', error);
      // Continue with registration if settings check fails
    }
  }, [navigate]);

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard');
    }
  }, [isAuthenticated, navigate]);

  // Handle email verification parameters
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const email = urlParams.get('email');
    const token = urlParams.get('token');
    const verified = urlParams.get('verified');

    if (email && token && verified === 'true') {
      // Email was verified, pre-fill and lock the email field
      setRegistrationData(prev => ({
        ...prev,
        email: decodeURIComponent(email)
      }));
      setVerificationToken(token);
      setEmailLocked(true);
      setSuccess(t('registration:messages.emailVerified'));
    } else if (!email && !token) {
      // No verification parameters, check if email verification is required
      checkEmailVerificationRequired();
    }
  }, [location.search, t, navigate, checkEmailVerificationRequired]);

  // Load available plans (with optional member type filtering)
  const loadPlans = useCallback(async (memberTypeId = null) => {
    try {
      // Use member type from parameter or current registration data
      const selectedMemberTypeId = memberTypeId || registrationData.memberType;
      
      const queryParams = new URLSearchParams();
      if (selectedMemberTypeId) {
        queryParams.append('memberType', selectedMemberTypeId);
      }
      
      const queryString = queryParams.toString();
      const url = `${API_URL}/registration/plans${queryString ? `?${queryString}` : ''}`;
      
      const response = await axios.get(url);
      if (response.data.success) {
        setAvailablePlans(response.data.data);
      }
    } catch (error) {
      console.error('Error loading plans:', error);
    }
  }, [registrationData.memberType]);

  // Load member types
  const loadMemberTypes = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/member-types?detailed=true`);
      if (response.data.success) {
        setMemberTypes(response.data.data);
      }
    } catch (error) {
      console.error('Error loading member types:', error);
    }
  }, []);

  // Validate existing session function
  const validateExistingSession = useCallback(async (sessionToken) => {
    try {
      const response = await axios.post(`${API_URL}/registration/validate-session`, {
        sessionToken: sessionToken
      });

      if (response.data.success) {
        // Session is still valid, advance to step 2
        setRegistrationData(prev => ({
          ...prev,
          sessionToken: sessionToken
        }));
        setActiveStep(1);
        setSuccess(t('registration:messages.welcomeBack'));
      }
    } catch (error) {
      // Session is expired or invalid, clear it
      sessionStorage.removeItem('registrationSessionToken');
      console.log('Session validation failed:', error.response?.data?.error);
    }
  }, [t]);

  // Load initial data and validate session
  useEffect(() => {
    loadPlans();
    loadMemberTypes();

    // Check for existing session token and validate it
    const sessionToken = sessionStorage.getItem('registrationSessionToken');
    if (sessionToken) {
      validateExistingSession(sessionToken);
    }
  }, [loadPlans, validateExistingSession, loadMemberTypes]);

  const handleInputChange = useCallback((field, value) => {
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      setRegistrationData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }));
    } else {
      setRegistrationData(prev => ({
        ...prev,
        [field]: value
      }));
    }
    
    if (field === 'memberType') {
      const memberType = memberTypes.find(mt => mt._id === value);
      setSelectedMemberType(memberType);
      
      if (memberType) {
        setRegistrationData(prev => ({
          ...prev,
          memberType: value
        }));
      }
      
      // Reset custom fields data when switching member types
      setCustomFieldsData({});
      setError('');
      return; // Early return since we've already handled the field update
    }
    
    setError('');
  }, [memberTypes]);
  
  const handleCustomFieldChange = useCallback((fieldName, value) => {
    setCustomFieldsData(prev => ({
      ...prev,
      [fieldName]: value
    }));
  }, []);

  // Validation functions
  const getFieldValidationState = useCallback((field, value) => {
    if (!value) return null;
    
    switch (field) {
      case 'email':
        return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
      case 'password':
        return /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z\d\s])[\S]{8,}$/.test(value);
      case 'tckn':
        return /^\d{11}$/.test(value) && value !== '00000000000';
      case 'phone':
        return /^(\+90|0)?[5][0-9]{9}$/.test(value);
      case 'address.street':
        return value && value.trim().length >= 10;
      case 'address.city':
        return value && value.trim().length >= 3 && !/\d/.test(value);
      case 'address.district':
        return value && value.trim().length >= 3 && !/\d/.test(value);
      default:
        return value.length > 0;
    }
  }, []);

  const validateStep1 = () => {
    const { firstName, lastName, username, email, password, confirmPassword, memberType, tckn, address, emergencyContact, healthDisclaimer, ageConfirmation } = registrationData;

    if (!firstName || !lastName || !username || !email || !password || !memberType || !tckn || !address?.street || !address?.city || !address?.district) {
      return t('registration:validation.fillAllRequired');
    }
    
    if (password !== confirmPassword) {
      return t('registration:validation.passwordsNoMatch');
    }
    
    if (!getFieldValidationState('password', password)) {
      return t('registration:validation.passwordRequirements');
    }
    
    if (!getFieldValidationState('tckn', tckn)) {
      return t('registration:validation.tcknInvalid');
    }

    if (address?.street && !getFieldValidationState('address.street', address.street)) {
      return t('registration:validation.addressTooShort');
    }

    if (address?.city && !getFieldValidationState('address.city', address.city)) {
      return t('registration:validation.cityTooShortOrHasNumbers');
    }

    if (address?.district && !getFieldValidationState('address.district', address.district)) {
      return t('registration:validation.districtTooShortOrHasNumbers');
    }

    if (!emergencyContact.name || !emergencyContact.relationship || !emergencyContact.phone) {
      return t('registration:validation.emergencyContactRequired');
    }
    
    if (!getFieldValidationState('phone', emergencyContact.phone)) {
      return t('registration:validation.phoneInvalid');
    }
    
    
    if (!healthDisclaimer.accepted) {
      return t('registration:validation.healthDisclaimerRequired');
    }

    if (!ageConfirmation.accepted) {
      return t('registration:validation.ageConfirmationRequired');
    }

    // Validate custom fields
    if (selectedMemberType && selectedMemberType.customFields) {
      for (const field of selectedMemberType.customFields) {
        if (field.required) {
          const value = customFieldsData[field.fieldName];
          if (!value || (typeof value === 'string' && value.trim() === '')) {
            return t('registration:validation.customFieldRequired', { fieldLabel: field.fieldLabel });
          }
        }
      }
    }
    
    return null;
  };

  const handleStep1Submit = async () => {
    const validationError = validateStep1();
    if (validationError) {
      setError(validationError);
      return;
    }

    // Store registration data for later use after KVKK approval
    const step1Data = {
      username: registrationData.username,
      email: registrationData.email,
      password: registrationData.password,
      firstName: registrationData.firstName,
      lastName: registrationData.lastName,
      userType: registrationData.userType,
      memberType: registrationData.memberType,
      tckn: registrationData.tckn,
      address: registrationData.address,
      emergencyContact: registrationData.emergencyContact,
      universityProfile: registrationData.universityProfile,
      healthDisclaimer: {
        accepted: registrationData.healthDisclaimer.accepted
      },
      ageConfirmation: {
        accepted: registrationData.ageConfirmation.accepted
      },
      customFieldsData: customFieldsData,
      // Include email verification token if available
      emailVerificationToken: verificationToken || undefined
    };

    setPendingRegistrationData(step1Data);
    setError('');
    
    // Generate temporary session token for KVKK approval
    const tempToken = 'temp_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
    setTempSessionToken(tempToken);
    
    // Show KVKK modal first
    setShowKvkkModal(true);
  };

  // Handle KVKK approval completion
  const handleKvkkApproval = async (approvalData) => {
    if (!pendingRegistrationData) {
      setError('Registration data not found. Please try again.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await axios.post(`${API_URL}/registration/step1-session`, pendingRegistrationData);
      
      if (response.data.success) {
        // Store session token instead of creating user immediately
        const sessionToken = response.data.data.sessionToken;
        sessionStorage.setItem('registrationSessionToken', sessionToken);
        
        setRegistrationData(prev => ({
          ...prev,
          sessionToken: sessionToken,
          kvkkApproval: approvalData
        }));
        
        setSuccess(t('registration:messages.kvkkCompleted'));
        setActiveStep(1);
        await loadPlans(pendingRegistrationData.memberType);
        
        // Clear pending data
        setPendingRegistrationData(null);
      }
    } catch (error) {
      console.error('Step 1 error:', error);
      console.error('Error details:', error.response?.data);
      
      // Show detailed validation errors if available
      if (error.response?.data?.details && Array.isArray(error.response.data.details)) {
        const validationErrors = error.response.data.details.map(err => err.msg).join(', ');
        setError(`Validation failed: ${validationErrors}`);
      } else {
        setError(handleApiError(error));
      }
    } finally {
      setLoading(false);
    }
  };

  // Handle KVKK modal close without approval
  const handleKvkkModalClose = () => {
    setShowKvkkModal(false);
    setPendingRegistrationData(null);
  };

  const validateStep2 = () => {
    if (!registrationData.selectedPlan) {
      return t('registration:validation.selectPlan');
    }
    return null;
  };

  const handleStep2Submit = async () => {
    const validationError = validateStep2();
    if (validationError) {
      setError(validationError);
      return;
    }

    setLoading(true);
    setError('');

    try {
      const sessionToken = sessionStorage.getItem('registrationSessionToken') || registrationData.sessionToken;
      
      if (!sessionToken) {
        setError(t('registration:validation.sessionExpired'));
        setActiveStep(0);
        return;
      }

      const response = await axios.post(`${API_URL}/registration/step2-session`, {
        sessionToken: sessionToken,
        planId: registrationData.selectedPlan._id
      });
      
      if (response.data.success) {
        // Now we have the actual user account created
        setRegistrationData(prev => ({
          ...prev,
          applicationId: response.data.data.registration.applicationId,
          registrationStatus: 'pending_verification'
        }));
        
        // Store authentication token for the created user
        localStorage.setItem('token', response.data.data.token);
        axios.defaults.headers.common['Authorization'] = `Bearer ${response.data.data.token}`;
        
        // Clear session token as it's no longer needed
        sessionStorage.removeItem('registrationSessionToken');
        
        setSuccess(t('registration:messages.registrationSuccess'));
        setActiveStep(2);
      }
    } catch (error) {
      console.error('Step 2 error:', error);
      
      // Handle session-specific errors
      if (error.response?.data?.error?.includes('session token')) {
        setError(t('registration:validation.sessionExpired'));
        setActiveStep(0);
        sessionStorage.removeItem('registrationSessionToken');
      } else {
        setError(handleApiError(error));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    setActiveStep(prev => prev - 1);
    setError('');
  };


  // Form fields component
  const FormFields = (
    <Box>

      <Grid container spacing={2} sx={{ gap: 2, my: 2 }}>
        {/* Row 1: TCKN, First Name, Last Name */}
        <Grid size={12} >
          <Paper elevation={2} sx={{ p: 1, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
            <Typography variant="h7" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <PersonIcon color="primary" />
              {t('registration:sections.personalInfo')}
            </Typography>

            <Box sx={{ display: 'flex', mt: 3, gap: 3, flexWrap: 'wrap' }}>
              <TextField
                label={t('registration:fields.firstName')}
                value={registrationData.firstName}
                onChange={(e) => handleInputChange('firstName', e.target.value)}
                required
                data-testid="first-name-input"
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">
                        <PersonIcon color="action" />
                      </InputAdornment>
                    )
                  }
                }}
                sx={{ width: inputWidth }}
              />
              <TextField
                label={t('registration:fields.lastName')}
                value={registrationData.lastName}
                onChange={(e) => handleInputChange('lastName', e.target.value)}
                required
                data-testid="last-name-input"
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">
                        <PersonIcon color="action" />
                      </InputAdornment>
                    )
                  }
                }}
                sx={{ width: inputWidth }}
              />
            </Box>
            <Box sx={{ display: 'flex', mt: 3, gap: 3, flexWrap: 'wrap' }}>
              <TextField
                label={t('registration:fields.username')}
                value={registrationData.username}
                onChange={(e) => handleInputChange('username', e.target.value)}
                required
                data-testid="username-input"
                sx={{ width: inputWidth }}
              />
              <TextField
                label={t('registration:fields.email')}
                type="email"
                value={registrationData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                required
                disabled={emailLocked}
                data-testid="email-input"
                error={registrationData.email && !emailLocked && !getFieldValidationState('email', registrationData.email)}
                helperText={emailLocked ? t('registration:messages.emailLocked') : undefined}
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">
                        <EmailIcon color={emailLocked ? "success" : "action"} />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        {emailLocked ? (
                          <CheckCircleIcon color="success" />
                        ) : (
                          registrationData.email && (
                            getFieldValidationState('email', registrationData.email) ?
                              <CheckCircleIcon color="success" /> :
                              <ErrorIcon color="error" />
                          )
                        )}
                      </InputAdornment>
                    )
                  }
                }}
                sx={{
                  width: inputWidth,
                  '& .MuiInputBase-root': {
                    backgroundColor: emailLocked ? 'action.selected' : 'inherit'
                  },
                  '& .MuiInputBase-input': {
                    color: emailLocked ? 'black !important' : 'inherit',
                    WebkitTextFillColor: emailLocked ? 'black !important' : 'inherit'
                  },
                  '& .MuiInputLabel-root': {
                    color: emailLocked ? '#6b7280 !important' : 'inherit'
                  },
                  '& .MuiFormHelperText-root': {
                    color: emailLocked ? 'black !important' : 'inherit'
                  }
                }}
              />
            </Box>
            <Box sx={{ display: 'flex', mt: 3, gap: 3, flexWrap: 'wrap' }}>
              <TextField
                label={t('registration:fields.password')}
                type={showPassword ? 'text' : 'password'}
                value={registrationData.password}
                onChange={(e) => handleInputChange('password', e.target.value)}
                required
                data-testid="password-input"
                error={registrationData.password && !getFieldValidationState('password', registrationData.password)}
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">
                        <SecurityIcon color="action" />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    )
                  }
                }}
                sx={{ width: inputWidth }}
              />
              <TextField
                label={t('registration:fields.confirmPassword')}
                type={showConfirmPassword ? 'text' : 'password'}
                value={registrationData.confirmPassword}
                onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
                required
                data-testid="confirm-password-input"
                error={registrationData.confirmPassword && registrationData.password !== registrationData.confirmPassword}
                slotProps={{
                  input: {
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                          edge="end"
                        >
                          {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    )
                  }
                }}
                sx={{ width: inputWidth }}
              />
            </Box>
            <Box sx={{ display: 'flex', gap: 3, mt: 3, flexWrap: 'wrap' }}>
              <TextField
                label={t('registration:fields.tckn')}
                value={registrationData.tckn}
                onChange={(e) => handleInputChange('tckn', e.target.value.replace(/\D/g, '').slice(0, 11))}
                required
                data-testid="tckn-input"
                error={registrationData.tckn && !getFieldValidationState('tckn', registrationData.tckn)}
                sx={{ width: inputWidth }}
              />
              <TextField
                required
                label={t('registration:fields.streetAddress')}
                value={registrationData.address.street}
                onChange={(e) => handleInputChange('address.street', e.target.value)}
                placeholder="e.g. Atatürk Caddesi No: 123"
                data-testid="street-input"
                error={registrationData.address.street && !getFieldValidationState('address.street', registrationData.address.street)}
                sx={{ width: inputWidth }}
              />
            </Box>
            <Box sx={{ display: 'flex', gap: 3, mt: 3, flexWrap: 'wrap' }}>
              <TextField
                required
                label={t('registration:fields.district')}
                value={registrationData.address.district}
                onChange={(e) => handleInputChange('address.district', e.target.value)}
                placeholder="e.g. Çankaya"
                data-testid="district-input"
                error={registrationData.address.district && !getFieldValidationState('address.district', registrationData.address.district)}
                sx={{ width: inputWidth }}
              />
              <TextField
                required
                label={t('registration:fields.city')}
                value={registrationData.address.city}
                onChange={(e) => handleInputChange('address.city', e.target.value)}
                placeholder="e.g. Ankara"
                data-testid="city-input"
                error={registrationData.address.city && !getFieldValidationState('address.city', registrationData.address.city)}
                sx={{ width: inputWidth }}
              />
              <TextField
                label={t('registration:fields.postalCode')}
                value={registrationData.address.postalCode}
                onChange={(e) => handleInputChange('address.postalCode', e.target.value)}
                placeholder="e.g. 06420"
                data-testid="postal-code-input"
                sx={{ width: inputWidth }}
              />
            </Box>
            <Box sx={{ display: 'flex', mt: 3, gap: 3, flexWrap: 'wrap' }}>
              <FormControl required sx={{ width: inputWidth }}>
                <InputLabel>{t('registration:fields.memberType')}</InputLabel>
                <Select
                  value={registrationData.memberType}
                  onChange={(e) => handleInputChange('memberType', e.target.value)}
                  label={t('registration:fields.memberType')}
                  data-testid="member-type-select"
                >
                {memberTypes.map((memberType) => {
                  // Get appropriate icon based on member type code
                  const getIcon = (code) => {
                    switch (code) {
                      case 'student':
                        return <SchoolIcon />;
                      case 'technopolis_employee':
                        return <WorkIcon />;
                      case 'external':
                        return <PersonIcon />;
                      default:
                        return <PersonIcon />;
                    }
                  };

                  return (
                    <MenuItem key={memberType._id} value={memberType._id}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {getIcon(memberType.code)}
                        {memberType.name}
                      </Box>
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
            </Box>
          </Paper>
        </Grid>

        {/* Custom Fields Section */}
        {selectedMemberType && selectedMemberType.customFields && selectedMemberType.customFields.length > 0 && (
          <Grid size={12}>
            <Paper elevation={4} sx={{ p: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
              <Typography variant="h7" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                {t('registration:sections.additionalInfo')}
              </Typography>
              <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                {selectedMemberType.customFields
                  .sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0))
                  .map((field) => {
                    const fieldValue = customFieldsData[field.fieldName] || '';
                    
                    return (
                      <Box key={field.fieldName}>
                        {field.fieldType === 'text' && (
                          <TextField
                            label={field.fieldLabel + (field.required ? ' *' : '')}
                            value={fieldValue}
                            onChange={(e) => handleCustomFieldChange(field.fieldName, e.target.value)}
                            required={field.required}
                            placeholder={field.placeholder}
                            helperText={field.helpText}
                            data-testid={`${field.fieldName}-input`}
                            sx={{ width: inputWidth }}
                          />
                        )}
                        
                        {field.fieldType === 'number' && (
                          <TextField
                            label={field.fieldLabel + (field.required ? ' *' : '')}
                            type="number"
                            value={fieldValue}
                            onChange={(e) => handleCustomFieldChange(field.fieldName, e.target.value)}
                            required={field.required}
                            placeholder={field.placeholder}
                            helperText={field.helpText}
                            data-testid={`${field.fieldName}-input`}
                            sx={{ width: inputWidth }}
                          />
                        )}
                        
                        {field.fieldType === 'email' && (
                          <TextField
                            label={field.fieldLabel + (field.required ? ' *' : '')}
                            type="email"
                            value={fieldValue}
                            onChange={(e) => handleCustomFieldChange(field.fieldName, e.target.value)}
                            required={field.required}
                            placeholder={field.placeholder}
                            helperText={field.helpText}
                            sx={{ width: inputWidth }}
                          />
                        )}
                        
                        {field.fieldType === 'textarea' && (
                          <TextField
                            label={field.fieldLabel + (field.required ? ' *' : '')}
                            multiline
                            rows={3}
                            value={fieldValue}
                            onChange={(e) => handleCustomFieldChange(field.fieldName, e.target.value)}
                            required={field.required}
                            placeholder={field.placeholder}
                            helperText={field.helpText}
                            sx={{ width: inputWidth }}
                          />
                        )}
                        
                        {field.fieldType === 'select' && field.options && field.options.length > 0 && (
                          <FormControl required={field.required} sx={{ width: inputWidth }}>
                            <InputLabel>{field.fieldLabel + (field.required ? ' *' : '')}</InputLabel>
                            <Select
                              value={fieldValue}
                              onChange={(e) => handleCustomFieldChange(field.fieldName, e.target.value)}
                              label={field.fieldLabel + (field.required ? ' *' : '')}
                            >
                              {field.options.map((option) => (
                                <MenuItem key={option.value} value={option.value}>
                                  {option.label}
                                </MenuItem>
                              ))}
                            </Select>
                            {field.helpText && (
                              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, px: 1.5 }}>
                                {field.helpText}
                              </Typography>
                            )}
                          </FormControl>
                        )}
                        
                        {field.fieldType === 'checkbox' && (
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={fieldValue === true || fieldValue === 'true'}
                                onChange={(e) => handleCustomFieldChange(field.fieldName, e.target.checked)}
                              />
                            }
                            label={field.fieldLabel + (field.required ? ' *' : '')}
                            sx={{ width: inputWidth }}
                          />
                        )}
                        
                        {field.fieldType === 'date' && (
                          <TextField
                            label={field.fieldLabel + (field.required ? ' *' : '')}
                            type="date"
                            value={fieldValue}
                            onChange={(e) => handleCustomFieldChange(field.fieldName, e.target.value)}
                            required={field.required}
                            helperText={field.helpText}
                            InputLabelProps={{ shrink: true }}
                            sx={{ width: inputWidth }}
                          />
                        )}
                      </Box>
                    );
                  })}
              </Box>
            </Paper>
          </Grid>
        )}

        {/* Last Row: Emergency Contact */}
        <Grid size={12}>
          <Paper elevation={4} sx={{ p: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
            <Typography variant="h7" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
              <PhoneIcon color="primary" />
              {t('registration:sections.emergencyContact')}
            </Typography>
            <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
              <TextField
                label={t('registration:fields.contactName')}
                value={registrationData.emergencyContact.name}
                onChange={(e) => handleInputChange('emergencyContact.name', e.target.value)}
                required
                data-testid="emergency-contact-name-input"
                sx={{ width: inputWidth }}
              />
              <TextField
                label={t('registration:fields.relationship')}
                value={registrationData.emergencyContact.relationship}
                onChange={(e) => handleInputChange('emergencyContact.relationship', e.target.value)}
                required
                data-testid="emergency-contact-relationship-input"
                placeholder={t('registration:placeholders.relationshipExample')}
                sx={{ width: inputWidth }}
              />
              <TextField
                label={t('registration:fields.phone')}
                value={registrationData.emergencyContact.phone}
                onChange={(e) => handleInputChange('emergencyContact.phone', e.target.value)}
                required
                data-testid="emergency-contact-phone-input"
                error={registrationData.emergencyContact.phone && !getFieldValidationState('phone', registrationData.emergencyContact.phone)}
                placeholder={t('registration:placeholders.phoneExample')}
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">
                        <PhoneIcon color="action" />
                      </InputAdornment>
                    )
                  }
                }}
                sx={{ width: inputWidth }}
              />
            </Box>
          </Paper>
        </Grid>

        {/* Health Declaration Section */}
        <Grid size={12}>
          <Paper elevation={4} sx={{ p: 2, borderRadius: 2, backgroundColor: 'grey.50', border: '1px solid', borderColor: 'divider' }}>
            <Typography variant="h7" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
              <MonitorHeartIcon color="primary" />
              {t('registration:sections.healthDeclaration')}
            </Typography>
            <FormControlLabel
              control={
                <Checkbox
                  size="medium"
                  checked={registrationData.healthDisclaimer.accepted}
                  onChange={(e) => handleInputChange('healthDisclaimer.accepted', e.target.checked)}
                  data-testid="health-disclaimer-checkbox"
                />
              }
              required
              label={t('registration:healthDisclaimer.text')}
            />
            <FormControlLabel
              control={
                <Checkbox
                  size="medium"
                  checked={registrationData.ageConfirmation.accepted}
                  onChange={(e) => handleInputChange('ageConfirmation.accepted', e.target.checked)}
                  data-testid="age-confirmation-checkbox"
                />
              }
              required
              label={t('registration:ageConfirmation.text')}
              sx={{ mt: 2 }}
            />
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );

  // Plan selection step
  const PlanSelectionStep = () => {
    return (
      <Box>
        <Typography variant="h6" gutterBottom>
          {t('registration:planSelection.title')}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          {t('registration:planSelection.subtitle')}
        </Typography>
        
        <Grid container spacing={3}>
          {availablePlans.map((plan, index) => {
            const isSelected = registrationData.selectedPlan?._id === plan._id;
            const isRecommended = index === 1;
            
            return (
              <Grid size={{ xs: 12, sm: 6, md: 4 }} key={plan._id}>
                <Card 
                  sx={{ 
                    position: 'relative',
                    cursor: 'pointer',
                    border: 2,
                    borderColor: isSelected ? 'primary.main' : 'transparent',
                    transform: isSelected ? 'scale(1.02)' : 'scale(1)',
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                      transform: 'scale(1.02)',
                      boxShadow: 6,
                      borderColor: isSelected ? 'primary.main' : 'primary.light'
                    },
                    ...(isRecommended && {
                      border: '2px solid',
                      borderColor: 'secondary.main'
                    })
                  }}
                  onClick={() => handleInputChange('selectedPlan', plan)}
                >
                  {isRecommended && (
                    <Chip
                      label={t('registration:planSelection.mostPopular')}
                      color="secondary"
                      size="small"
                      sx={{
                        position: 'absolute',
                        top: -8,
                        left: '50%',
                        transform: 'translateX(-50%)',
                        zIndex: 1,
                        fontWeight: 'bold'
                      }}
                    />
                  )}
                  
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <Typography variant="h6" component="div">
                        {plan.name}
                      </Typography>
                      {isSelected && <CheckCircleIcon color="primary" />}
                    </Box>
                    
                    <Box sx={{ mb: 2 }}>
                      {plan.calculatedPrice !== undefined && plan.price > plan.calculatedPrice && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                          <Typography 
                            variant="body2" 
                            color="text.secondary" 
                            sx={{ textDecoration: 'line-through' }}
                          >
                            ₺{plan.price}
                          </Typography>
                          <Chip 
                            label={`${Math.round(((plan.price - plan.calculatedPrice) / plan.price) * 100)}% OFF`}
                            color="success"
                            size="small"
                          />
                        </Box>
                      )}
                      <Typography variant="h4" color="primary" sx={{ fontWeight: 'bold' }}>
                        ₺{plan.calculatedPrice !== undefined ? plan.calculatedPrice : plan.price}
                        <Typography component="span" variant="body2" color="text.secondary">
                          /{plan.duration} days
                        </Typography>
                      </Typography>
                    </Box>
                    
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      {plan.description}
                    </Typography>
                    
                    {(() => {
                      // Use allFeatures if available (member type specific), otherwise use base features
                      const featuresToShow = plan.allFeatures || plan.features;

                      return featuresToShow && featuresToShow.length > 0 && (
                        <Box>
                          {featuresToShow.map((feature, featureIndex) => (
                            <Box key={featureIndex} sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                              <CheckIcon color="success" fontSize="small" sx={{ mr: 1 }} />
                              <Typography variant="body2">
                                {feature}
                              </Typography>
                            </Box>
                          ))}
                        </Box>
                      );
                    })()}

                    {/* Facility Access Display */}
                    {plan.facilityAccess && plan.facilityAccess.length > 0 && (
                      <Box sx={{ mt: 2 }}>
                        <FacilityAccessDisplay
                          facilityAccess={plan.facilityAccess}
                          compact={true}
                        />
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      </Box>
    );
  };

  // Verification step
  const VerificationStep = () => (
    <Box textAlign="center" py={4}>
      <CheckCircleIcon sx={{ fontSize: 80, color: 'warning.main', mb: 3 }} />
      <Typography variant="h5" gutterBottom>
        {t('registration:verification.title')}
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        {t('registration:verification.description')}
      </Typography>
      
      <Card sx={{ maxWidth: 400, mx: 'auto', mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            {t('registration:verification.applicationDetails')}
          </Typography>
          <Box sx={{ textAlign: 'left' }}>
            <Typography variant="body2" color="text.secondary" data-testid="application-id">
              {t('registration:verification.applicationId')} <strong>{registrationData.applicationId}</strong>
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('registration:verification.selectedPlan')} <strong>{registrationData.selectedPlan?.name}</strong>
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('registration:verification.amount')} <strong>₺{registrationData.selectedPlan?.price}</strong>
            </Typography>
          </Box>
        </CardContent>
      </Card>
      
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t('registration:verification.emailNotification')}
      </Typography>
      
      <Button
        variant="contained"
        onClick={() => navigate('/login')}
        sx={{ mt: 2 }}
      >
        {t('registration:buttons.goToLogin')}
      </Button>
    </Box>
  );

  // Success step
  const SuccessStep = () => (
    <Box textAlign="center" py={4}>
      <CheckCircleIcon sx={{ fontSize: 80, color: 'success.main', mb: 3 }} />
      <Typography variant="h4" gutterBottom color="success.main">
        {t('registration:success.title')}
      </Typography>
      <Typography variant="body1" color="text.secondary" mb={4}>
        {t('registration:success.welcomeMessage')}
      </Typography>
      <Button
        variant="contained"
        size="large"
        onClick={() => navigate('/login')}
        startIcon={<ArrowForwardIcon />}
      >
        {t('registration:buttons.continueToLogin')}
      </Button>
    </Box>
  );

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ 
      display: 'flex', 
      minHeight: '100vh',
      bgcolor: theme => theme.palette.background.default,
      position: 'relative',
      overflow: 'hidden'
    }}>
      {/* Decorative background elements */}
      <Box
        sx={{
          position: 'absolute',
          width: '500px',
          height: '500px',
          borderRadius: '50%',
          background: theme => theme.palette.accent.mint,
          filter: 'blur(150px)',
          opacity: 0.07,
          top: '-100px',
          right: '-100px',
          zIndex: 0
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          width: '400px',
          height: '400px',
          borderRadius: '50%',
          background: theme => theme.palette.accent.yellow,
          filter: 'blur(120px)',
          opacity: 0.05,
          bottom: '-50px',
          left: '-50px',
          zIndex: 0
        }}
      />
      
      {/* Left Panel - Welcome */}
      {!isMobile && (
        <Box sx={{ width: '40%', height: '100vh', zIndex: 1 }}>
          <WelcomePanel />
        </Box>
      )}

      {/* Right Panel - Form */}
      <Box 
        component={motion.div}
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.5 }}
        sx={{ 
          flex: 1,
          height: '100vh',
          overflow: 'auto',
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'center',
          p: isMobile ? 1 : 2,
          pt: isMobile ? 1 : 2,
          zIndex: 1
        }}
      >
        <Paper
          elevation={4}
          sx={{
            px: 2,
            width: '100%',
            maxWidth: '900px',
            borderRadius: 3,
            background: 'rgba(255, 255, 255, 0.9)',
            backdropFilter: 'blur(10px)',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
            position: 'relative',
            overflow: 'hidden'
          }}
        >
          {/* Mobile header */}
          {isMobile && (
            <Box 
              component={motion.div}
              initial={{ y: -20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.6 }}
              sx={{ 
                textAlign: "center", 
                mb: 4,
                position: 'relative'
              }}
            >
              <Avatar
                src="/logo192.png"
                alt="MetuMotion Logo"
                sx={{ 
                  width: 80, 
                  height: 80, 
                  mx: 'auto',
                  mb: 2,
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
                }}
              />
              <Typography 
                variant="h4" 
                fontWeight="bold" 
                gutterBottom
                sx={{ 
                  color: theme => theme.palette.primary.main,
                  textShadow: '0 1px 2px rgba(0, 0, 0, 0.1)'
                }}
              >
                {t('registration:headers.joinTitle')}
              </Typography>
              <Divider sx={{ width: '60px', mx: 'auto', my: 2, borderColor: theme => theme.palette.primary.light }} />
            </Box>
          )}

          {!isMobile && (
            <Box 
              component={motion.div}
              initial={{ y: -20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.6 }}
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}
            >
              <Typography 
                variant="h5" 
                gutterBottom 
                sx={{ 
                  mt: 1, 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 1, 
                  mb: 2,
                  fontWeight: 600,
                  color: theme => theme.palette.primary.dark
                }}
              >
                {t('registration:headers.pageTitle')}
              </Typography>
            </Box>
          )}

          {/* Steps indicator */}
          <Box 
            component={motion.div}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            sx={{ 
              mb: 4,
              px: 2,
              py: 2,
              borderRadius: 2,
              backgroundColor: 'rgba(255, 255, 255, 0.7)',
              backdropFilter: 'blur(5px)',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)'
            }}
          >
            <Stepper 
              activeStep={activeStep} 
              alternativeLabel={!isMobile}
              data-testid="registration-stepper"
              orientation={isMobile ? 'vertical' : 'horizontal'}
              sx={{ 
                mt: 1,
                '& .MuiStepConnector-line': {
                  borderColor: theme => theme.palette.primary.light,
                  borderTopWidth: 3,
                  borderRadius: 4
                },
                '& .MuiStepConnector-root.Mui-active .MuiStepConnector-line': {
                  borderColor: theme => theme.palette.primary.main
                },
                '& .MuiStepConnector-root.Mui-completed .MuiStepConnector-line': {
                  borderColor: theme => theme.palette.primary.main
                }
              }}
            >
              {steps.map((step, index) => {
                const StepIcon = step.icon;
                return (
                  <Step key={step.label} completed={index < activeStep}>
                    <StepLabel 
                      icon={
                        <Avatar
                          sx={{
                            width: 40,
                            height: 40,
                            bgcolor: index <= activeStep ? 'primary.main' : 'grey.300',
                            color: index <= activeStep ? 'white' : 'text.secondary',
                            transition: 'all 0.3s ease'
                          }}
                        >
                          <StepIcon />
                        </Avatar>
                      }
                      sx={{
                        '& .MuiStepLabel-label': {
                          typography: 'body2',
                          fontWeight: index <= activeStep ? 600 : 400,
                          color: index <= activeStep ? 'primary.main' : 'text.secondary',
                          mt: 1
                        }
                      }}
                    >
                      {step.label}
                    </StepLabel>
                  </Step>
                );
              })}
            </Stepper>
          </Box>

          {/* Alerts */}
          {error && (
            <Alert severity="error" sx={{ mb: 3 }} data-testid="error-alert">
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mb: 3 }} data-testid="success-alert">
              {success}
            </Alert>
          )}

          {/* Form content */}
          {activeStep === 0 && FormFields}
          {activeStep === 1 && <PlanSelectionStep />}
          {activeStep === 2 && <VerificationStep />}
          {activeStep === 3 && <SuccessStep />}

          {/* Form actions */}
          {activeStep < 2 && (
            <Box mt={4}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                <Button
                  disabled={activeStep === 0}
                  onClick={handleBack}
                  size="large"
                  data-testid="back-button"
                  sx={{ minWidth: 100 }}
                >
                  {t('registration:buttons.back')}
                </Button>
                <Button
                  variant="contained"
                  size="large"
                  onClick={activeStep === 0 ? handleStep1Submit : handleStep2Submit}
                  disabled={loading}
                  data-testid={activeStep === 0 ? "next-button" : "submit-button"}
                  sx={{ py: 1.5, fontSize: '1.1rem', flex: 1 }}
                >
                  {loading ? (
                    <CircularProgress size={24} color="inherit" />
                  ) : (
                    activeStep === 0 ? t('registration:buttons.next') : t('registration:buttons.submitApplication')
                  )}
                </Button>
              </Box>

            </Box>
          )}
        </Paper>
      </Box>
      
      {/* KVKK Approval Modal */}
      <KvkkApprovalModal
        open={showKvkkModal}
        onClose={handleKvkkModalClose}
        onApproved={handleKvkkApproval}
        context="registration"
        sessionToken={tempSessionToken}
        title="Üyelik için Kişisel Veri İşleme Onayları"
      />
    </Box>
  );
};

export default RegisterPage;
